'use client';

import { useState, useEffect, useCallback } from 'react';

const EXPORT_COUNT_KEY = 'slidetoanki_export_count';
const TESTIMONIAL_GIVEN_KEY = 'slidetoanki_testimonial_given';
const TESTIMONIAL_TRIGGER_COUNT = 2;

/**
 * Hook to track export count for testimonial prompts.
 * Uses localStorage to persist count across sessions.
 */
export function useExportCount() {
  const [exportCount, setExportCount] = useState<number>(0);
  const [testimonialGiven, setTestimonialGivenState] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const storedCount = localStorage.getItem(EXPORT_COUNT_KEY);
    const storedTestimonialGiven = localStorage.getItem(TESTIMONIAL_GIVEN_KEY);
    
    if (storedCount) {
      setExportCount(parseInt(storedCount, 10) || 0);
    }
    
    setTestimonialGivenState(storedTestimonialGiven === 'true');
    setIsLoaded(true);
  }, []);

  // Increment export count
  const incrementExportCount = useCallback(() => {
    const current = parseInt(localStorage.getItem(EXPORT_COUNT_KEY) || '0', 10) || 0;
    const newCount = current + 1;
    localStorage.setItem(EXPORT_COUNT_KEY, String(newCount));
    setExportCount(newCount);
    return newCount;
  }, []);

  // Mark testimonial as given
  const setTestimonialGiven = useCallback(() => {
    setTestimonialGivenState(true);
    localStorage.setItem(TESTIMONIAL_GIVEN_KEY, 'true');
  }, []);

  // Check if we should show testimonial modal
  const shouldShowTestimonialModal = isLoaded && 
    exportCount >= TESTIMONIAL_TRIGGER_COUNT && 
    !testimonialGiven;

  return {
    exportCount,
    incrementExportCount,
    shouldShowTestimonialModal,
    testimonialGiven,
    setTestimonialGiven,
    isLoaded,
  };
}
